<script setup lang="ts">
/**
 * App Header Component
 *
 * Main application header with navigation and user menu
 */
import { MenuIcon } from 'lucide-vue-next';
import { useUIStore, useEntitlementsStore, useNotificationStore, useAuthStore } from '@/stores';
import logoImage from '@/assets/timeless-medical-logo.png';
import { NotificationBell } from '@/shared/components/notifications';
import AppUserMenu from './AppUserMenu.vue';
import KeyInspectorToggleButton from './KeyInspectorToggleButton.vue';
import { useTranslation } from 'i18next-vue';
import { useRouter } from 'vue-router';
import { SmartSearchBox } from '@tms-phoenix/ui-vue';
import type { SmartSearchFetchResult } from '@tms-phoenix/ui-vue';
import { fetchSearchResults, resolveSmartSearchLangCode } from '@/modules/search/services/search.api';

const { t } = useTranslation();
const uiStore = useUIStore();
const entitlementsStore = useEntitlementsStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();

/** Shrink header when the recall alert banner is occupying space below it. */
const hasRecallBanner = computed(() => notificationStore.isRecallBannerVisible);
const router = useRouter();

// Smart Search — fetch adapter
// API now returns localized smart-search text based on the supplied tenant lang_code.
async function handleFetchResults(query: string): Promise<SmartSearchFetchResult> {
  const langCode = resolveSmartSearchLangCode(authStore.defaultLanguage);
  const response = await fetchSearchResults(query, langCode);
  const items = response.data;
  const pagination = (response as unknown as Record<string, unknown>).pagination as { total?: number } | undefined;
  return {
    results: items.map((item) => {
      return {
        index_id: item.id,
        module_code: item.module,
        module_label: item.category ?? item.module,
        feature: item.title,
        display_title: item.title,
        display_description: item.description,
        page_url: item.route,
        tags: [],
      };
    }),
    total: pagination?.total ?? items.length,
  };
}

function handleNavigate(url: string) {
  router.push(url);
}

// Language display — future scope (commented out in template)
// const LANGUAGE_MAP: Record<string, { code: string; name: string; flagUrl: string }> = {
//   en: { code: 'US', name: 'English', flagUrl: 'https://flagcdn.com/w40/us.png' },
//   'en-GB': { code: 'GB', name: 'English (UK)', flagUrl: 'https://flagcdn.com/w40/gb.png' },
//   'en-US': { code: 'US', name: 'English (US)', flagUrl: 'https://flagcdn.com/w40/us.png' },
//   de: { code: 'DE', name: 'Deutsch', flagUrl: 'https://flagcdn.com/w40/de.png' },
//   fr: { code: 'FR', name: 'Français', flagUrl: 'https://flagcdn.com/w40/fr.png' },
// };
// const currentLanguage = computed(() => {
//   const lang = authStore.defaultLanguage || 'en';
//   return LANGUAGE_MAP[lang] ?? LANGUAGE_MAP['en'];
// });

function toggleDesktopSidebar() {
  uiStore.toggleSidebarCollapse();
}

function toggleSidebar() {
  uiStore.toggleSidebar();
}
</script>

<template>
  <header
    class="custom-header sticky top-0 z-40 flex shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-4 transition-[height] duration-200"
    :class="hasRecallBanner ? 'h-12' : 'h-16'"
  >
    <!-- Mobile menu button -->
    <button
      type="button"
      class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
      @click="toggleSidebar"
    >
      <span class="sr-only">{{ t('nav.openSidebar') }}</span>
      <MenuIcon
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Desktop sidebar toggle button -->
    <button
      type="button"
      class="hidden lg:flex -m-2.5 p-2.5 text-gray-700 hover:text-primary-600 transition-colors items-center"
      :title="uiStore.sidebarCollapsed ? t('nav.showSidebar') : t('nav.hideSidebar')"
      @click="toggleDesktopSidebar"
    >
      <MenuIcon
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Logo -->
    <RouterLink
      to="/"
      class="flex items-center"
    >
      <img
        :src="logoImage"
        alt="Timeless Medical"
        class="h-8 w-auto"
      >
    </RouterLink>

    <!-- Separator -->
    <!-- <div class="h-6 w-px bg-gray-200 lg:hidden dark:bg-gray-700" aria-hidden="true" /> -->

    <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
      <!-- Search (placeholder) -->
      <div class="flex flex-1" />

      <!-- User menu -->
      <div class="flex items-center gap-x-4 lg:gap-x-4">
        <!-- Smart Search -->
        <SmartSearchBox
          :fetch-results="handleFetchResults"
          :placeholder="t('nav.searchPlaceholder')"
          :empty-label="t('nav.noSearchResults', { defaultValue: 'No results found for \'{query}\'' })"
          :search-icon-label="t('common.search')"
          :clear-button-title="t('common.clear')"
          :clear-icon-label="t('ui.searchBar.clearSearch')"
          @navigate="handleNavigate"
        />

        <!-- Moon Icon (future scope) -->
        <!-- <button
          type="button"
          class="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          :title="t('nav.theme')"
        >
          <img
            :src="darkModeIcon"
            alt="Theme"
            class="h-5 w-5"
          >
        </button> -->

        <!-- Language Display (future scope) -->
        <!-- <div
          class="flex items-center gap-2 rounded-lg px-2 py-1.5"
          :title="currentLanguage.name"
        >
          <img
            :src="currentLanguage.flagUrl"
            :alt="currentLanguage.name"
            class="h-4 w-6 object-cover rounded"
          >
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ currentLanguage.code }}</span>
        </div> -->

        <!-- Notification Bell (live SSE-driven) -->
        <NotificationBell v-if="entitlementsStore.hasEntitlement('RECALL')" />

        <!-- Key inspector toggle — see KeyInspectorToggleButton's own docblock -->
        <KeyInspectorToggleButton />

        <!-- User Profile Menu -->
        <AppUserMenu />
      </div>
    </div>
  </header>
</template>

<style scoped>
.custom-header {
  background: linear-gradient(0deg, rgba(228, 247, 251, 0.20) 100%, rgba(255, 255, 255, 0.20) 100%, rgba(228, 247, 251, 0.20) 100%), white;
}

.cheron-down {
  color: var(--color-gray-900);
}
</style>
